import React, { useState } from "react";
import numeral from "numeral";
import { TabPane, Row, Col } from "reactstrap";
import Constant from "../../../../constants";
import SitePerformanceEstimation from "../../../../components/SitePerformanceEstimation";
import StorePlanningMap from "../../../StorePlanning/pages/Map";
import SiteComparison from "../../../SiteComparison/pages";
import SiteCannibalizationModal from "../SiteCannibalizationModal";
import SiteInBuildingRentalCalculationModal from "../SiteInBuildingRentalCalculationModal";
import SiteWholeRentalCalculationModal from "../SiteWholeRentalCalculationModal";
import SiteRevenueShareRentalCalculationModal from "../SiteRevenueShareRentalCalculationModal";
import "./index.scss";
import { Button } from "shared";
import Map from "pages/Map";
import t from "utils/Lang";

interface SummaryTabInputProps {
  isView: boolean;
  siteData: any;
  siteId: any;
}

export default function SummaryTab({
  isView,
  siteData,
  siteId,
}: SummaryTabInputProps) {
  const [showRentalCalculationModal, setShowRentalCalculationModal] =
    useState<boolean>(false);
  const [showCannibalizationModal, setShowCannibalizationModal] =
    useState<boolean>(false);

  const toggleCannibalizationModal = () => {
    setShowCannibalizationModal(!showCannibalizationModal);
  };
  const onSiteCannibalizationModalCancel = () => {
    setShowCannibalizationModal(false);
  };

  const toggleRentalCalculationMode = () => {
    setShowRentalCalculationModal(!showRentalCalculationModal);
  };

  const uiLayoutConfig: Array<any> = [
    {
      type: "row",
      title: t().site_summary_tab__pricing,
      items: [
        {
          icon: "attach_money",
          title: t().site_summary_tab__initial_lease_rate,
          value: siteData.data?.lease_rate || siteData.data?.initial_lease_rate,
          unit: "VND",
          isNumeric: true,
          childComponent: siteData.data?.rental_calculation_method ? (
            <a
              onClick={toggleRentalCalculationMode}
              style={styles.viewInBuildingCalculation}
            >
              {t().site_summary_tab__calculation_method}:{" "}
              {siteData.data?.rental_calculation_method_name}
            </a>
          ) : null,
        },
        {
          icon: "calendar_today",
          title: t().site_summary_tab__lease_term_year,
          value: siteData.data?.lease_term_year,
          unit: "năm",
        },
        {
          icon: "trending_up",
          title: t().site_summary_tab__increase_rate_year_period,
          value: siteData.data?.increase_rate_year_period,
          unit: "năm",
        },
        {
          icon: "update",
          title: t().site_listing__site_owner_reply_due_date,
          value: siteData.data?.contact_before_date,
        },
        {
          icon: "sports_hockey",
          title: t().site_summary_tab__estimate_cannibalization,
          childComponent: (
            <Button
              label="Cannibalization"
              outline
              type="primary"
              onClick={toggleCannibalizationModal}
            />
          ),
        },
      ],
    },
    {
      type: "row",
      title: t().site_summary_tab__area_and_size,
      items: [
        {
          icon: "arrow_forward",
          title: t().site_summary_tab__site_width,
          value: siteData.data?.site_width,
          unit: "m",
        },
        {
          icon: "arrow_downward",
          title: t().site_summary_tab__site_length,
          value: siteData.data?.site_length,
          unit: "m",
        },
        {
          icon: "aspect_ratio",
          title: t().site_summary_tab__total_area,
          value: siteData.data?.total_area,
          unit: "m2",
        },
        {
          icon: "api",
          title: t().site_summary_tab__usable_area,
          value: siteData.data?.area,
          unit: "m2",
        },
      ],
    },
    {
      type: "row",
      title: t().site_summary_tab__parking,
      items: [
        {
          icon: "local_parking",
          title: t().site_summary_tab__primary_parking,
          value: siteData?.data?.primary_motorbike_parking_site_type?.name,
        },
        {
          icon: "directions_bike",
          title: t().site_summary_tab__secondary_parking,
          value: siteData?.data?.secondary_motorbike_parking_site_type?.name,
        },
        {
          icon: "bike_scooter",
          title: t().site_summary_tab__motorbike_parking_capacity,
          value: siteData?.data?.primary_motorbike_parking_site_capacity,
          unit: t().site_summary_tab__parking_unit,
        },
      ],
    },
    {
      type: "row",
      title: t().site_summary_tab__site,
      items: [
        {
          icon: "satellite",
          title: t().site_summary_tab__site_description,
          value: siteData?.data?.site_description,
          isRichText: true,
        },
        {
          icon: "corporate_fare",
          title: t().site_summary_tab__primary_site_surrounding_area,
          value: siteData?.data?.primary_site_surrounding_area_type?.name,
        },
        {
          icon: "local_library",
          title: t().site_summary_tab__secondary_site_surrounding_area,
          value: siteData?.data?.secondary_site_surrounding_area_type?.name,
        },
        {
          icon: "remove_red_eye",
          title: t().site_summary_tab__visibility,
          value: siteData?.data?.visibility,
        },
        {
          icon: "accessibility_new",
          title: t().site_summary_tab__accessibility,
          value: siteData?.data?.accessibility,
        },
      ],
    },
  ];
  const siteRentalCalculationMode = siteData?.data?.rental_calculation_method;
  return (
    <TabPane tabId="summary_tab">
      <h4 className="site-summary__label-title">
        {t().site_summary_tab__site_map}
      </h4>
      <Row>
        <Col xs={12}>
          {siteData?.data &&
          siteData?.data?.latitude &&
          siteData?.data?.longitude &&
          Number.isFinite(siteData?.data?.latitude) &&
          siteData?.data?.latitude >= -90 &&
          siteData?.data?.latitude <= 90 &&
          Number.isFinite(siteData?.data?.longitude) &&
          siteData?.data?.longitude >= -180 &&
          siteData?.data?.longitude <= 180 ? (
            <div style={styles.mapDiv}>
              <Map
                latitude={parseFloat(siteData?.data?.latitude)}
                longitude={parseFloat(siteData?.data?.longitude)}
                embedMode
              />
            </div>
          ) : (
            <div style={styles.noMapDiv}>
              <p>{t().site_summary_tab__map_error}</p>
            </div>
          )}
        </Col>
      </Row>

      {uiLayoutConfig.map((rowLayout, rowIndex) => (
        <Row key={rowIndex}>
          <Col xs={12} className="site-summary__label-container">
            <h4 className="site-summary__label-title">{rowLayout.title}</h4>
            <div className="site-summary__body">
              {rowLayout.items?.map((item: any, index: any) => (
                <Row key={index} className="site-summary__label-item">
                  <Col
                    xs={item.isRichText ? 12 : 6}
                    sm={item.isRichText ? 12 : 4}
                    className="site-summary__label-header"
                  >
                    <span className="site-summary__label-text">
                      {item.title}:
                    </span>
                  </Col>
                  <Col
                    xs={item.isRichText ? 12 : 6}
                    sm={item.isRichText ? 12 : 8}
                  >
                    <span
                      className={`${
                        item.isRichText
                          ? "site-summary__rich-text-paragraph"
                          : ""
                      }`}
                    >
                      {item.value &&
                        ` ${
                          item.isNumeric
                            ? numeral(item.value).format("0,0")
                            : item.value
                        }`}
                    </span>
                    {item.value && item.unit && ` ${item.unit}`}
                    {item.childComponent ? item.childComponent : null}
                  </Col>
                </Row>
              ))}
            </div>
          </Col>
          <Row></Row>
        </Row>
      ))}
      <Row>
        <Col xs={12} className="site-summary__label-container">
          <h4 className="site-summary__label-title">
            {t().site_detail__tab_financial_projection}
          </h4>
        </Col>
        <Col xs={12}>
          <SitePerformanceEstimation siteId={siteId} />
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="site-summary__label-container">
          <h4 className="site-summary__label-title">
            {t().site_summary_tab__site_comparison}
          </h4>
        </Col>
        <Col xs={12}>
          <SiteComparison siteIdInput={siteId} viewOnly />
        </Col>
      </Row>
      {siteRentalCalculationMode === "area" && showRentalCalculationModal && (
        <SiteInBuildingRentalCalculationModal
          siteId={siteData.data.id || siteId}
          isView
          isShow={showRentalCalculationModal}
          onCancelCallback={() => setShowRentalCalculationModal(false)}
        />
      )}
      {siteRentalCalculationMode === "whole" && showRentalCalculationModal && (
        <SiteWholeRentalCalculationModal
          siteId={siteData.data.id || siteId}
          isView
          isShow={showRentalCalculationModal}
          onCancelCallback={() => setShowRentalCalculationModal(false)}
        />
      )}
      {siteRentalCalculationMode === "revenue_share" &&
        showRentalCalculationModal && (
          <SiteRevenueShareRentalCalculationModal
            siteId={siteData.data.id || siteId}
            isView
            isShow={showRentalCalculationModal}
            onCancelCallback={() => setShowRentalCalculationModal(false)}
          />
        )}
      {showCannibalizationModal && (
        <SiteCannibalizationModal
          siteId={siteData.data.id || siteId}
          isShow={showCannibalizationModal}
          isView
          toggle={toggleCannibalizationModal}
          onCancelCallback={onSiteCannibalizationModalCancel}
        />
      )}
    </TabPane>
  );
}

const styles = {
  container: {
    paddingTop: 12,
    paddingBottom: 12,
  },
  mapDiv: {
    marginTop: 12,
  },
  noMapDiv: {
    padding: 15,
  },
  viewInBuildingCalculation: {
    marginLeft: 12,
    color: "var(--primary)",
    cursor: "pointer",
  },
  richTextParagraph: {
    // whiteSpace: 'pre-line',
  },
};
